import { patchState, signalStore, withComputed, withHooks, withState } from '@ngrx/signals';
import { withLogger } from '@core/stores/features/logger.feature';
import { computed, inject } from '@angular/core';
import { KeycloakProfile, KeycloakResourceAccess, KeycloakRoles, KeycloakUserInfo } from 'keycloak-js';
import { KeycloakService } from '../services/keycloak.service';
import { InitService } from '../services/init.service';
import { distinctUntilChanged, filter, firstValueFrom, map, startWith } from 'rxjs';
import {
  applicationInitialized,
  loadInitConfig,
  loadUserInfo,
  loadUserProfile,
  showApplicationError,
} from '@core/events/application.events';
import { Dispatcher, on, withReducer } from '@ngrx/signals/events';
import { KeycloakConfiguration } from '@models/online-reputation.models';
import { FilterStore } from '@core/stores/filter.store';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

export type ApplicationState = {
  config?: KeycloakConfiguration;
  userInfo: KeycloakUserInfo;
  userProfile: KeycloakProfile;
  realmAccess: KeycloakRoles;
  resourceAccess: KeycloakResourceAccess;
  clientId: string;
  languages: string[];
  currentLanguage: string;
  authenticated: boolean;
  currentUrl: string;
  lastApplicationError: Error | undefined
};

const PREFERRED_LANGUAGE_STORAGE_KEY = 'online-reputation.preferredLanguage';

export const initialState: ApplicationState = {
  config: undefined,
  userInfo: { sub: '' },
  userProfile: {},
  realmAccess: { roles: [] },
  resourceAccess: {},
  clientId: '',
  languages: ['en', 'it'], // supported languages
  currentLanguage: 'en',
  authenticated: false,
  currentUrl: '',
  lastApplicationError: undefined
};

export const ApplicationStore = signalStore(
  // Provided at root level as single shared instance
  { providedIn: 'root' },
  withState<ApplicationState>(initialState),
  withLogger('ApplicationStore'),
  withComputed((store) => ({
    userFullname: computed(() => store.userInfo()['name'] ?
      `${ store.userInfo()['name'] }` : ''),
    userFirstName: computed(() => store.userInfo()['given_name']),
    userLastName: computed(() => store.userInfo()['family_name']),
    userEmail: computed(() => store.userInfo()['email']),
    userEmailVerified: computed(() => store.userInfo()['email_verified'] || false),
    userPermissions: computed(() => store.resourceAccess()[store.clientId()]?.roles || []),
  })),
  withReducer(
    on(loadInitConfig, ({ payload: config }) => ({ config })),
    on(loadUserInfo, ({ payload: userInfo }) => ({ userInfo })),
    on(loadUserProfile,
      ({ payload: { userProfile, realmAccess, resourceAccess, clientId } }) =>
        ({ userProfile, realmAccess, resourceAccess, clientId })),
    on(showApplicationError, ({ payload: lastApplicationError }) => ({ lastApplicationError }))
  ),
  withHooks({
    async onInit(store,
                 router = inject(Router),
                 translateService = inject(TranslateService),
                 dispatcher = inject(Dispatcher),
                 initService = inject(InitService),
                 keycloakService = inject(KeycloakService),
                 filterStore = inject(FilterStore)) {

      // Restore back the preferred language
      const currentLanguage = localStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY) || 'en';
      patchState(store, { currentLanguage });
      translateService.use(currentLanguage);

      // On language change updates the store
      translateService.onLangChange
      .subscribe(event => {
        patchState(store, { currentLanguage: event.lang });
        localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, event.lang);
      });

      const config = await firstValueFrom(initService.initConfig());
      const authenticated = await keycloakService.init(config);
      keycloakService.onTokenExpired(async () => {
        console.log('Session expired, logging out...');
        await keycloakService.logout();
      });

      console.log('After init', authenticated);
      if (authenticated) {
        patchState(store, { authenticated: true });

        // Loads the user info
        const userInfo = await keycloakService.loadUserInfo();

        // Loads the user profile
        const userProfile = await keycloakService.loadUserProfile();
        const realmAccess = keycloakService.realmAccess();
        const resourceAccess = keycloakService.resourceAccess();
        const clientId = keycloakService.clientId();

        router.events
        .pipe(
          startWith(router.url),
          filter((event) => typeof event === 'string' || event instanceof NavigationEnd),
          map(() => router.url),
          distinctUntilChanged()
        )
        .subscribe((currentUrl: string) => {
          keycloakService.updateToken(5).then(function (refreshed) {
            if (refreshed) {
              console.log('Token was successfully refreshed');
            } else {
              console.log('Token is still valid');
            }
          }).catch(function () {
            console.log('Failed to refresh the token, or the session has expired');
          });
          patchState(store, { currentUrl });
        });

        dispatcher.dispatch(loadInitConfig(config));
        dispatcher.dispatch(loadUserInfo(userInfo));
        dispatcher.dispatch(loadUserProfile({ userProfile, realmAccess, resourceAccess, clientId }));

        console.log('Application initialized');
        dispatcher.dispatch(applicationInitialized(filterStore.filter()));
      } else {
        console.error('User not authenticated!');
      }
    }
  }),
);
